import { ArrowRight } from "lucide-react";

// Wizard Step 2 — optionally copy an existing role's screen-access matrix
// onto the role being created. Purely presentational; parent owns state.
export default function FetchAccessView({
  roles,
  wizardRoleId,
  shouldFetchAccess,
  setShouldFetchAccess,
  fetchFromRoleId,
  setFetchFromRoleId,
  onCopyPermissions,
  onPrevious,
  onNext,
}) {
  const fetchableRoles = roles.filter(
    (r) => r.type === "custom" && r.id !== wizardRoleId,
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto p-4 sm:p-5">
        <div className="mx-auto w-full max-w-4xl rounded-t-table-lg border border-gray-200 bg-[#fafafa] shadow-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">
                Fetch Access from Other Role
              </h3>
              <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                Step 2
              </span>
            </div>
            <span className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
              Optional
            </span>
          </div>

          <div className="space-y-6 bg-white p-5 sm:p-6">
            <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4 sm:p-5">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                Permission Source
              </p>
              <label className="block text-sm font-medium text-gray-700">
                Do you want to fetch access from another role?
              </label>

              <div className="mt-3 space-y-2">
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 hover:bg-gray-50">
                  <input
                    type="radio"
                    name="shouldFetchAccess"
                    value="yes"
                    checked={shouldFetchAccess === true}
                    onChange={() => setShouldFetchAccess(true)}
                    className="h-4 w-4 text-redhalo-red focus:ring-redhalo-red/30"
                  />
                  <span className="text-sm font-medium text-gray-800">Yes</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 hover:bg-gray-50">
                  <input
                    type="radio"
                    name="shouldFetchAccess"
                    value="no"
                    checked={shouldFetchAccess === false}
                    onChange={() => setShouldFetchAccess(false)}
                    className="h-4 w-4 text-redhalo-red focus:ring-redhalo-red/30"
                  />
                  <span className="text-sm font-medium text-gray-800">No</span>
                </label>
              </div>
            </div>

            {shouldFetchAccess === true && (
              <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4 sm:p-5">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Select Source Role
                </p>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Select role to fetch access from
                </label>
                <span
                  className={`mb-3 inline-block rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                    fetchableRoles.length > 0
                      ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border border-amber-200 bg-amber-50 text-amber-700"
                  }`}
                >
                  {fetchableRoles.length > 0
                    ? `${fetchableRoles.length} source role${fetchableRoles.length > 1 ? "s" : ""} available`
                    : "No source roles available"}
                </span>

                <select
                  value={fetchFromRoleId || ""}
                  onChange={(e) => setFetchFromRoleId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-redhalo-red focus:ring-2 focus:ring-redhalo-red/30"
                >
                  <option value="">Select Role</option>
                  {fetchableRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>

                {fetchFromRoleId && (
                  <button
                    type="button"
                    onClick={onCopyPermissions}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-redhalo-red px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    <ArrowRight className="h-4 w-4" />
                    <span>Fetch Access</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-300 bg-gray-200/80 px-4 py-3">
        <button
          type="button"
          onClick={onPrevious}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-lg bg-redhalo-red px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={shouldFetchAccess === null}
        >
          Next: Screen Access
        </button>
      </div>
    </div>
  );
}
